/**
 * La barrera de errores.
 *
 * Existe por un fallo real de esta misma tarde: una celda pintaba un `Date` que
 * Eden había deserializado, React tiró *«Objects are not valid as a React
 * child»*, y **la aplicación entera se quedó en blanco**. Sin barrera, cualquier
 * error de pintado de una celda apaga el panel.
 *
 * Una pantalla en blanco es el peor estado de error posible: no dice qué pasó, no
 * deja reintentar y no deja ni copiar una referencia. `BFF_BO.md` §6 exige un
 * estado de error de verdad; esto es el que cubre lo que no se previó.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  readonly children: ReactNode;
}

interface Estado {
  readonly error: Error | null;
}

export class Barrera extends Component<Props, Estado> {
  override state: Estado = { error: null };

  static getDerivedStateFromError(error: Error): Estado {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // A la consola y no a un servicio: cuando haya uno, este es el sitio.
    console.error('[panel] error de pintado', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <main className="lienzo">
        <div className="hueco hueco-error" role="alert">
          <h2>Algo se ha roto al pintar esta pantalla</h2>
          <p>
            No es culpa tuya y tus datos están bien: el fallo es nuestro y está
            en la interfaz. Vuelve a cargar; si sigue pasando, mándanos el texto
            de abajo.
          </p>
          <div className="hueco-acciones">
            <button type="button" onClick={() => window.location.reload()}>
              Volver a cargar
            </button>
            <button
              type="button"
              className="btn-plano"
              onClick={() =>
                void navigator.clipboard?.writeText(
                  `${error.name}: ${error.message}`
                )
              }
            >
              Copiar el fallo
            </button>
          </div>
          <p className="traza">
            <code>
              {error.name}: {error.message}
            </code>
          </p>
        </div>
      </main>
    );
  }
}
